import { useEffect, useState } from 'react'
import { Api } from '../api/Assinafy'
import { brandFromTheme, shouldLoadSignerBrand } from './signerBrand'

const cache = new Map()
const pending = new Map()
const BRAND_VARS = [
  '--signer-button-background',
  '--signer-button-background-pressed',
  '--signer-button-background-soft',
  '--signer-button-text',
]
let rootUsers = 0

const paintRootBrand = (style) => {
  const root = document.documentElement
  root.classList.add('signer-brand')
  BRAND_VARS.forEach((name) => {
    root.style.setProperty(name, style[name])
  })
}

const clearRootBrand = () => {
  const root = document.documentElement
  root.classList.remove('signer-brand')
  BRAND_VARS.forEach((name) => {
    root.style.removeProperty(name)
  })
}

export const resetSignerBrandCache = () => {
  cache.clear()
  pending.clear()
  rootUsers = 0
  clearRootBrand()
}

const loadSignerBrand = (accessCode) => {
  if (cache.has(accessCode)) {
    return Promise.resolve(cache.get(accessCode))
  }

  if (!pending.has(accessCode)) {
    let timeoutId
    const timeout = new Promise((resolve) => {
      timeoutId = setTimeout(() => resolve(null), 2000)
    })
    const theme = Api('signer/theme')(accessCode)
      .then((response) => brandFromTheme(response?.data?.data, accessCode))
      .catch(() => null)
    const request = Promise.race([theme, timeout])
      .then((brand) => {
        cache.set(accessCode, brand)

        return brand
      })
      .finally(() => {
        clearTimeout(timeoutId)
        pending.delete(accessCode)
      })

    pending.set(accessCode, request)
  }

  return pending.get(accessCode)
}

const present = (status, brand) => ({
  status,
  accountName: brand?.accountName || '',
  logoSrc: brand?.logoSrc || null,
  style: brand?.style || null,
})

const initialState = (accessCode) => {
  if (!shouldLoadSignerBrand(accessCode)) {
    return { status: 'skipped', brand: null }
  }

  if (cache.has(accessCode)) {
    return { status: 'ready', brand: cache.get(accessCode) }
  }

  return { status: 'loading', brand: null }
}

export const useSignerBrand = (accessCode) => {
  const [state, setState] = useState(() => initialState(accessCode))

  useEffect(() => {
    if (!shouldLoadSignerBrand(accessCode)) {
      setState({ status: 'skipped', brand: null })

      return undefined
    }

    if (cache.has(accessCode)) {
      setState({ status: 'ready', brand: cache.get(accessCode) })

      return undefined
    }

    let active = true
    setState({ status: 'loading', brand: null })

    loadSignerBrand(accessCode).then((value) => {
      if (active) {
        setState({ status: 'ready', brand: value })
      }
    })

    return () => {
      active = false
    }
  }, [accessCode])

  useEffect(() => {
    const style = state.brand?.style
    if (!style) {
      return undefined
    }

    rootUsers += 1
    paintRootBrand(style)

    return () => {
      rootUsers -= 1
      if (rootUsers === 0) {
        clearRootBrand()
      }
    }
  }, [state.brand])

  return present(state.status, state.brand)
}
